import React, { useRef, useState, useEffect } from 'react';
import {
  AddNewGoalButton,
  GoalsRow,
  GoalsRowDivider,
  GoalsTableContainer,
} from './style/GoalsTable.styled';
import { GoalCard } from '../GoalCard/GoalCard';
import {
  GoalsPerStatus,
  StatusDisplayText,
} from 'src/tabs/models/GoalsInterfaces';
import { appearance } from 'src/hooks/useAlertMessage';
import { Add20Regular as AddRegular } from '@fluentui/react-icons';
import {
  Drafts16Regular as DraftsIcon,
  CheckmarkCircle16Regular as CheckmarkCircleIcon,
  ArrowSync16Filled as ArrowSyncIcon,
  Flag20Regular as FlagIcon,
} from '@fluentui/react-icons';
import { isMobile } from 'react-device-detect';
import { GoalsHeaderTitle } from '../GoalsRowHeader/style/GoalsRowHeader.styled';

interface GoalsTableProps {
  goals: GoalsPerStatus;
  selectedPeriod: string;
  getAllUserGoals: () => void;
  showAlert: (message: string, appearance: appearance) => void;
  setIsAdding: React.Dispatch<React.SetStateAction<boolean>>;
  isAdding: boolean;
  requestStatusMessage: string;
  requestStatusAppearance: appearance;
  hideAlert: () => void;
  isReadOnlyPeriod: boolean;
  target?: HTMLElement;
}

export const GoalsTable = ({
  goals,
  selectedPeriod,
  getAllUserGoals,
  showAlert,
  setIsAdding,
  isAdding,
  isReadOnlyPeriod,
  target,
}: GoalsTableProps) => {
  const goalsRowRef = useRef<HTMLDivElement>(null);
  const [isEditingCard, setIsEditingCard] = useState(-1);

  const scrollToCard = (top: number, isVisible: boolean) => {
    if (isVisible) return;
    goalsRowRef.current.scrollTop = top;
  };

  const handleAddNewGoal = () => {
    setIsAdding(true);

    goalsRowRef.current.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  useEffect(() => {
    if (isMobile) {
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
    }
  }, []);

  return (
    <GoalsTableContainer>
      <GoalsRow ref={goalsRowRef}>
        {isMobile && (
          <GoalsHeaderTitle>
            <DraftsIcon />
            Draft
          </GoalsHeaderTitle>
        )}

        {!isAdding && !isMobile && !isReadOnlyPeriod && (
          <AddNewGoalButton onClick={handleAddNewGoal}>
            <AddRegular />
            &nbsp;Add a new goal
          </AddNewGoalButton>
        )}
        {isAdding && (
          <GoalCard
            isAdding={true}
            visible={isAdding}
            goal={null}
            status={null}
            rowName={StatusDisplayText.DRAFT}
            discardAdding={() => setIsAdding(false)}
            selectedPeriod={selectedPeriod}
            getAllUserGoals={getAllUserGoals}
            showAlert={showAlert}
            isReadOnlyPeriod={isReadOnlyPeriod}
            target={target}
          />
        )}

        {goals.draft?.map((goal, index) => (
          <GoalCard
            isAdding={false}
            isEditing={index === isEditingCard}
            key={goal.cra0e_employeegoalid}
            goal={goal}
            status={goal.statuscode}
            rowName={StatusDisplayText.DRAFT}
            setIsEditingCard={() => setIsEditingCard(index)}
            discardEditingCard={() => setIsEditingCard(-1)}
            selectedPeriod={selectedPeriod}
            getAllUserGoals={getAllUserGoals}
            isReadOnlyPeriod={isReadOnlyPeriod}
            target={target}
            scrollToCard={scrollToCard}
          />
        ))}
      </GoalsRow>
      {!isMobile && <GoalsRowDivider />}
      <GoalsRow>
        {isMobile && (
          <GoalsHeaderTitle>
            <CheckmarkCircleIcon />
            Approved
          </GoalsHeaderTitle>
        )}

        {goals.approved?.map((goal) => (
          <GoalCard
            isAdding={false}
            key={goal.cra0e_employeegoalid}
            goal={goal}
            status={goal.statuscode}
            rowName={StatusDisplayText.APPROVED}
            selectedPeriod={selectedPeriod}
            getAllUserGoals={getAllUserGoals}
            isReadOnlyPeriod={isReadOnlyPeriod}
            target={target}
          />
        ))}
      </GoalsRow>
      {!isMobile && <GoalsRowDivider />}
      <GoalsRow>
        {isMobile && (
          <GoalsHeaderTitle>
            <ArrowSyncIcon />
            In progress
          </GoalsHeaderTitle>
        )}

        {goals.inProgress?.map((goal) => (
          <GoalCard
            isAdding={false}
            key={goal.cra0e_employeegoalid}
            goal={goal}
            status={goal.statuscode}
            rowName={StatusDisplayText.IN_PROGRESS}
            selectedPeriod={selectedPeriod}
            getAllUserGoals={getAllUserGoals}
            isReadOnlyPeriod={isReadOnlyPeriod}
            target={target}
          />
        ))}
      </GoalsRow>
      {!isMobile && <GoalsRowDivider />}
      <GoalsRow>
        {isMobile && (
          <GoalsHeaderTitle>
            <FlagIcon />
            Closed
          </GoalsHeaderTitle>
        )}
        {goals.closed?.map((goal) => (
          <GoalCard
            isAdding={false}
            key={goal.cra0e_employeegoalid}
            goal={goal}
            status={goal.statuscode}
            rowName={StatusDisplayText.CLOSED}
            selectedPeriod={selectedPeriod}
            getAllUserGoals={getAllUserGoals}
            isReadOnlyPeriod={isReadOnlyPeriod}
            target={target}
          />
        ))}
      </GoalsRow>
    </GoalsTableContainer>
  );
};
